import {GraphQLErrorResponse} from '../types/auth';
import {ApolloClient, createHttpLink, from, Observable} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {getAuth, signOut} from 'firebase/auth';
import {refreshAuthToken} from './auth';
import toast from 'react-hot-toast';
import {APOLLO_DEFAULT_OPTIONS, APOLLO_QUERY_DEDUPLICATION} from './apolloDefaults';
import {createApolloCache} from './apolloCache';

// Create the client instance at the module level
let apolloClient: ApolloClient<any>;

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const error = err as GraphQLErrorResponse;
      const status = error.extensions?.violations?.[0]?.status;

      // Check if it's a 403 error and we haven't retried yet
      if (status === 403 && !operation.getContext().isRetry) {
        return new Observable(observer => {
          refreshAuthToken()
            .then(token => {
              if (!token) {
                // Handle failed token refresh
                const auth = getAuth();
                signOut(auth).then(() => {
                  // Clear Apollo cache
                  if (apolloClient) {
                    apolloClient.clearStore();
                  }
                  // Redirect to login
                  window.location.href = '/login?error=session_expired';
                });
                observer.error(new Error('Session expired. Please sign in again.'));
                return;
              }

              // Retry the operation with new token
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${token}`,
                },
                isRetry: true,
              });

              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch(error => {
              console.error('Token refresh failed:', error);
              observer.error(error);
            });
        });
      }

      console.error('GraphQL Error:', error);
    }
  }

  if (networkError) {
    console.error('Network Error:', networkError);
    
    // Check if it's a connection refused error (server down)
    if (networkError.message?.includes('Failed to fetch') || networkError.message?.includes('connection refused')) {
      toast.error('Unable to connect to server. Please try again later.', {
        id: 'server-down-error', // Prevent duplicate toasts
        duration: 5000
      });
      return;
    }

    // Check if user is offline
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your network.', {
        id: 'offline-error',
        duration: 5000
      });
      return;
    }

    // Generic network error
    toast.error('Network error occurred. Please try again.', {
      id: 'network-error',
      duration: 5000
    });
  }
});

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_GRAPHQL_ENDPOINT}`,
});

const authLink = setContext(async (_, { headers }) => {
  const auth = getAuth();
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  const tenancy = sessionStorage.getItem('yojo_tenancy');

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      ...(tenancy ? { 'x-yojo-tenancy': tenancy } : {}),
    }
  };
});

export const createApolloClient = () => {
  apolloClient = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: createApolloCache(),
    defaultOptions: APOLLO_DEFAULT_OPTIONS,
    queryDeduplication: APOLLO_QUERY_DEDUPLICATION,
  });

  return apolloClient;
};
